/**
 * Harlan SPA Navigation
 *
 * Lightweight AJAX page navigation that swaps <main> content
 * without a full page reload, so the Spotify player iframe
 * keeps playing across page changes.
 *
 * When page transitions are enabled, pages vanish into a blur
 * and new content emerges from emptiness — like a zen garden
 * sand painting being swept and redrawn.
 *
 * Only active when the persistent player is enabled.
 */
(function () {
  var MAIN_ID = 'MainContent';
  var VANISH_MS = 420;   // Time for sections to scatter (matches 0.42s CSS transition)

  // Read settings from script tag data attributes
  var scriptEl = document.currentScript || document.querySelector('script[src*="harlan-spa"]');
  var transitions = scriptEl && scriptEl.dataset.transitions === 'true';
  var transitionLabel = (scriptEl && scriptEl.dataset.transitionLabel) || 'accessing...';

  // --- Loading bar ---
  var loadingBar = document.createElement('div');
  loadingBar.className = 'harlan-loading-bar';
  document.body.appendChild(loadingBar);

  // --- Transition status label (per-character spans for blur wave) ---
  var statusEl = document.createElement('div');
  statusEl.className = 'harlan-transition-status';
  transitionLabel.split('').forEach(function (ch, i) {
    var span = document.createElement('span');
    span.textContent = ch;
    span.className = 'harlan-transition-char';
    span.style.setProperty('--char-i', i);
    statusEl.appendChild(span);
  });
  statusEl.style.setProperty('--char-total', transitionLabel.length);
  document.body.appendChild(statusEl);

  function startLoading() {
    loadingBar.classList.remove('harlan-loading-bar--done');
    loadingBar.style.width = '0%';
    loadingBar.style.opacity = '1';
    void loadingBar.offsetWidth;
    loadingBar.classList.add('harlan-loading-bar--active');
  }

  function finishLoading() {
    loadingBar.classList.remove('harlan-loading-bar--active');
    loadingBar.classList.add('harlan-loading-bar--done');
    setTimeout(function () {
      loadingBar.classList.remove('harlan-loading-bar--done');
      loadingBar.style.width = '0%';
      loadingBar.style.opacity = '1';
    }, 600);
  }

  // --- Page vanish / emerge ---
  function vanish() {
    return new Promise(function (resolve) {
      if (!transitions) return resolve();

      // Compute scatter vectors per section based on viewport position
      // Top → arc upward, bottom → drift down, middle → shift right
      var sections = document.querySelectorAll('#' + MAIN_ID + ' > .shopify-section');
      var vh = window.innerHeight;
      sections.forEach(function (section) {
        var rect = section.getBoundingClientRect();
        var centerY = (rect.top + rect.height / 2) / vh;
        centerY = Math.max(0, Math.min(1, centerY));
        var yOff = centerY - 0.5; // -0.5 (top) to 0.5 (bottom)

        // Y: top sections arc upward, bottom sections push downward
        var scatterY = yOff * -80;
        // X: bell curve — peaks at center (shift right), minimal at edges
        var xFactor = 1 - 4 * yOff * yOff;
        var scatterX = xFactor * 40;
        // Rotation: subtle arc tilt
        var scatterR = yOff * -3;

        section.style.setProperty('--scatter-x', scatterX.toFixed(1) + 'px');
        section.style.setProperty('--scatter-y', scatterY.toFixed(1) + 'px');
        section.style.setProperty('--scatter-r', scatterR.toFixed(2) + 'deg');
      });

      document.body.classList.add('harlan-vanish');
      setTimeout(resolve, VANISH_MS);
    });
  }

  function emerge() {
    if (!transitions) return;
    // Set stagger indices on new sections for palette-knife rake-in
    var sections = document.querySelectorAll('#' + MAIN_ID + ' > .shopify-section');
    for (var i = 0; i < sections.length; i++) {
      sections[i].style.setProperty('--section-index', i);
    }
    // Duration: enough for all sections to finish their staggered animation
    var emergeMs = Math.max(550, sections.length * 40 + 550);

    document.body.classList.remove('harlan-vanish');
    document.body.classList.add('harlan-emerge');
    setTimeout(function () {
      document.body.classList.remove('harlan-emerge');
    }, emergeMs);
  }

  // --- Wait for images before revealing new page ---
  // Finds all <img> in main content, waits for them to load (or timeout).
  // Prevents the flash of unloaded hero images during page transitions.
  function waitForImages() {
    if (!transitions) return Promise.resolve();
    var main = document.getElementById(MAIN_ID);
    if (!main) return Promise.resolve();

    var imgs = main.querySelectorAll('img[src]');
    var pending = [];
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete) continue;
      pending.push(new Promise(function (resolve) {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      }));
    }
    if (pending.length === 0) return Promise.resolve();

    // Race: wait for all images OR a max timeout (whichever comes first)
    var MAX_WAIT = 1200;
    return Promise.race([
      Promise.all(pending),
      new Promise(function (resolve) { setTimeout(resolve, MAX_WAIT); })
    ]);
  }

  // --- Content swap ---
  function doSwap(doc, url, isPop) {
    var newMain = doc.getElementById(MAIN_ID);
    var currentMain = document.getElementById(MAIN_ID);
    if (!newMain || !currentMain) throw new Error('no main');

    // Copy attributes (data-template, etc.)
    Array.prototype.slice.call(newMain.attributes).forEach(function (attr) {
      currentMain.setAttribute(attr.name, attr.value);
    });

    // Swap inner HTML
    currentMain.innerHTML = newMain.innerHTML;

    // Update page title
    document.title = doc.title;

    // Update URL
    if (!isPop) {
      history.pushState({ harlanSpa: true }, '', url);
    }

    // Scroll to top (or to hash target)
    var hash = new URL(url, location.href).hash;
    if (hash) {
      var target = document.querySelector(hash);
      if (target) { target.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);

    // Re-execute inline scripts so new sections initialize
    var scripts = currentMain.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');
      Array.prototype.slice.call(old.attributes).forEach(function (attr) {
        fresh.setAttribute(attr.name, attr.value);
      });
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    }

    // Sync header-component attributes (transparent, sticky, class)
    var newHeader = doc.getElementById('header-component');
    var currentHeader = document.getElementById('header-component');
    if (newHeader && currentHeader) {
      if (newHeader.hasAttribute('transparent')) {
        currentHeader.setAttribute('transparent', newHeader.getAttribute('transparent'));
      } else {
        currentHeader.removeAttribute('transparent');
      }
      if (newHeader.hasAttribute('sticky')) {
        currentHeader.setAttribute('sticky', newHeader.getAttribute('sticky'));
      } else {
        currentHeader.removeAttribute('sticky');
      }
      currentHeader.setAttribute('class', newHeader.getAttribute('class'));
    }

    // Sync body class — preserve our transition classes
    var newBody = doc.querySelector('body');
    if (newBody) {
      var keep = [];
      if (document.body.classList.contains('harlan-vanish')) keep.push('harlan-vanish');
      if (document.body.classList.contains('harlan-emerge')) keep.push('harlan-emerge');
      document.body.setAttribute('class', newBody.getAttribute('class'));
      keep.forEach(function (c) { document.body.classList.add(c); });
    }

    // Merge any new <style> tags from <head>
    var newStyles = doc.querySelectorAll('head style, head link[rel="stylesheet"]');
    newStyles.forEach(function (s) {
      var id = s.id || s.getAttribute('href');
      if (id && !document.querySelector('[id="' + CSS.escape(id) + '"], [href="' + id + '"]')) {
        document.head.appendChild(s.cloneNode(true));
      }
    });

    // Dispatch event so theme JS can re-hydrate sections
    document.dispatchEvent(new CustomEvent('harlan:navigate', { detail: { url: url } }));

    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) return;
    var sections = currentMain.querySelectorAll('.shopify-section[data-hydrated]');
    sections.forEach(function (s) { s.removeAttribute('data-hydrated'); });
  }

  // --- Main navigation ---
  function navigateTo(url, isPop) {
    startLoading();

    // Start fetch immediately (parallel with vanish animation)
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 8000);

    var fetchDone = fetch(url, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timeout);
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        return parser.parseFromString(html, 'text/html');
      });

    // Run vanish animation in parallel with fetch
    var vanishDone = vanish();

    // When BOTH vanish and fetch complete, swap content then wait for images
    Promise.all([fetchDone, vanishDone])
      .then(function (results) {
        var doc = results[0];
        doSwap(doc, url, isPop);

        // Wait for critical images to load before revealing the page.
        // This masks the flash of unloaded images, especially large hero images.
        return waitForImages().then(function () {
          emerge();
          finishLoading();
        });
      })
      .catch(function () {
        // Fallback: normal navigation
        document.body.classList.remove('harlan-vanish', 'harlan-emerge');
        finishLoading();
        clearTimeout(timeout);
        location.href = url;
      });
  }

  // --- Intercept link clicks ---
  document.addEventListener('click', function (e) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    if (e.defaultPrevented) return;

    var link = e.target.closest('a[href]');
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href) return;

    // Skip special links
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;
    if (href.charAt(0) === '#') return;
    if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;

    var url;
    try { url = new URL(href, location.href); } catch (ex) { return; }

    if (url.origin !== location.origin) return;
    if (/^\/(admin|checkout|account\/login|account\/register)/.test(url.pathname)) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    if (link.closest('form')) return;
    if (link.hasAttribute('on:click')) return;

    // Tap feedback on menu items + immediate close
    var menuItem = link.closest('.menu-drawer__menu-item--mainlist');
    if (menuItem) {
      menuItem.classList.add('harlan-nav-tapped');

      // Immediate visual feedback: fade out all menu items
      var menuContainer = document.getElementById('Details-menu-drawer-container');
      if (menuContainer) {
        menuContainer.classList.add('harlan-menu--navigating');
      }

      // Close menu after items fade (180ms) — don't wait for doSwap
      setTimeout(function () {
        var hd = document.querySelector('header-drawer');
        if (hd && typeof hd.close === 'function') hd.close();
      }, 180);
    }

    e.preventDefault();
    navigateTo(url.href, false);
  });

  // Handle back/forward
  window.addEventListener('popstate', function () {
    navigateTo(location.href, true);
  });
})();

/**
 * Harlan Menu — Explicit open/close (no toggle race conditions)
 *
 * Replaces the theme's toggle-based approach with explicit open() / close()
 * driven by a single source of truth (isMenuOpen). This prevents the race
 * condition where a stale onAnimationEnd → reset() callback from a previous
 * close clobbers the state of a new open.
 *
 * The theme's on:click="/toggle" routes through our overridden toggle(),
 * where we preventDefault() to suppress the native <details> toggle and
 * dispatch to openMenu/closeMenu based on our single source of truth.
 */
(function () {
  var details = document.getElementById('Details-menu-drawer-container');
  if (!details) return;

  var drawer = details.querySelector('.menu-drawer');
  var summary = details.querySelector('summary');
  if (!drawer || !summary) return;

  var isMenuOpen = false;
  var closeGen = 0; // generation counter — stale close callbacks check this

  // --- Blur wipe overlay (radial tinted circle trailing the drawer) ---
  // Lives in document.body at z-index 3: above <main> (auto), below
  // .header-section (z-index 4). Actual blurring done by filter on <main>.
  var blurWipe = document.createElement('div');
  blurWipe.className = 'harlan-blur-wipe';
  document.body.appendChild(blurWipe);

  // --- Luminance ---
  function relativeLuminance(r, g, b) {
    function lin(c) { var s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); }
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  }

  function getPageLuminance() {
    var bg = getComputedStyle(document.body).backgroundColor;
    var m = bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return relativeLuminance(+m[1], +m[2], +m[3]);
    return 0.95;
  }

  // --- Explicit OPEN ---
  function openMenu() {
    if (isMenuOpen) return;
    isMenuOpen = true;

    // Set [open] (needed for .menu-drawer-container[open] animation selectors)
    details.setAttribute('open', '');
    details.classList.add('menu-open');
    blurWipe.classList.remove('harlan-blur-closing');
    summary.setAttribute('aria-expanded', 'true');

    // Clip-path restart: snap to closed, then let CSS transition to open
    drawer.style.clipPath = 'circle(0% at 28px 28px)';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.style.clipPath = '';
      });
    });

    // Force-restart item animations (in case [open] was stale)
    setTimeout(function () {
      var items = details.querySelectorAll('.menu-drawer__animated-element');
      items.forEach(function (el) { el.style.animation = 'none'; });
      void details.offsetWidth;
      items.forEach(function (el) { el.style.animation = ''; });
    }, 20);

    // Clear lingering navigating state
    details.classList.remove('harlan-menu--navigating');

    // Adaptive contrast
    var lum = getPageLuminance();
    if (lum > 0.45) {
      details.classList.add('harlan-menu--light-bg');
    } else {
      details.classList.remove('harlan-menu--light-bg');
    }
  }

  // --- Explicit CLOSE ---
  function closeMenu() {
    if (!isMenuOpen) return;
    isMenuOpen = false;
    var gen = ++closeGen;

    details.classList.remove('menu-open');
    summary.setAttribute('aria-expanded', 'false');
    blurWipe.classList.add('harlan-blur-closing');

    // Remove [open] + clean up blur-closing after close animation (0.5s).
    // Generation counter protects against a fast re-open: if the user
    // opens the menu before this timer fires, gen !== closeGen and we bail.
    setTimeout(function () {
      if (closeGen !== gen) return;
      details.removeAttribute('open');
      blurWipe.classList.remove('harlan-blur-closing');
      details.classList.remove('harlan-menu--navigating');
    }, 500);
  }

  // --- No click listener needed ---
  // The theme's Component framework has a document-level capture handler that
  // reads on:click="/toggle" from <summary> and calls headerDrawer.toggle(event).
  // Since we override toggle() below, the framework routes directly to our code.
  // We call preventDefault() inside toggle() to suppress the native <details>
  // open/close (which would otherwise fight our explicit state management).

  // --- Escape key ---
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' && isMenuOpen) {
      e.stopPropagation(); // prevent theme's #onKeyUp from also firing
      closeMenu();
    }
  }, true);

  // --- Close button(s) inside drawer ---
  details.addEventListener('click', function (e) {
    if (e.target.closest('.menu-drawer__close-button') && isMenuOpen) {
      e.stopPropagation();
      closeMenu();
    }
  });

  // --- Public API: override theme's headerDrawer methods ---
  var headerDrawer = document.querySelector('header-drawer');
  if (headerDrawer) {
    headerDrawer.close = function () { closeMenu(); };
    headerDrawer.open = function () { openMenu(); };
    headerDrawer.toggle = function (event) {
      // Prevent native <details> toggle — the framework passes the click event
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (isMenuOpen) closeMenu(); else openMenu();
    };
    Object.defineProperty(headerDrawer, 'isOpen', {
      get: function () { return isMenuOpen; }
    });
  }
})();
